/**
 * Sync Queue Hook
 * Phase 2.1: Offline-first sync client that builds on Phase 1 collections
 * Maintains local-first UX while enabling multi-device sync
 */
import { useState, useEffect, useRef } from 'react';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from './useVesselCollections';
import { useLicense } from './useLicense';
import { createBaseRecord } from '../types/core';

export const useSyncQueue = () => {
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections, vesselId } = useVesselCollections();
  const { hasFeature, FEATURES } = useLicense();
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [pendingOps, setPendingOps] = useState(0);
  const [error, setError] = useState(null);
  const syncIntervalRef = useRef(null);

  // Check if sync is enabled
  const isSyncEnabled = hasFeature(FEATURES.CLOUD_SYNC);

  // Phase 2.6: Sync status loading temporarily disabled - will restore in Phase 3
  // Load sync status on mount
  useEffect(() => {
    console.warn('Phase 2.6: Sync status loading disabled - will return in Phase 3');
    setPendingOps(0);

    // const loadSyncStatus = async () => {
    //   try {
    //     const statusRecord = await collections.get('meta', 'sync_status');
    //     if (statusRecord?.data?.lastSync) {
    //       setLastSync(new Date(statusRecord.data.lastSync));
    //     }
    //
    //     // Count pending operations
    //     const pending = await collections.list('opsQueue');
    //     setPendingOps(pending.length);
    //   } catch (err) {
    //
    //   }
    // };
    //
    // loadSyncStatus();
  }, []);

  // Auto-sync setup
  useEffect(() => {
    if (!isSyncEnabled) return;

    // Sync when coming online
    const handleOnline = () => {
      if (navigator.onLine) {
        syncNow();
      }
    };

    // Sync when window gains focus
    const handleFocus = () => {
      if (navigator.onLine && !syncing) {
        syncNow();
      }
    };

    // Setup background sync interval
    syncIntervalRef.current = setInterval(() => {
      if (navigator.onLine && !syncing) {
        syncNow();
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isSyncEnabled, syncing]);

  // Phase 2.6: Queue operations temporarily disabled - will restore in Phase 3
  // Add operation to sync queue
  const queueOperation = async (collection, action, recordId, payload) => {
    console.warn('Phase 2.6: queueOperation disabled - will return in Phase 3');
    return;

    // if (!isSyncEnabled) return;
    //
    // const operation = createBaseRecord({
    //   id: `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    //   vesselId,
    //   collection,
    //   action, // 'create', 'update', 'delete'
    //   recordId,
    //   payloadHash: generatePayloadHash(payload),
    //   payload,
    //   origin: 'sync_queue'
    // });
    //
    // await collections.upsert('opsQueue', operation);
    // setPendingOps(prev => prev + 1);
  };

  // Phase 2.6: Drain queue temporarily disabled - will restore in Phase 3
  // Drain sync queue to server
  const drainQueue = async (batchSize = 100) => {
    console.warn('Phase 2.6: drainQueue disabled - will return in Phase 3');
    return { success: false, reason: 'disabled' };

    // if (!isSyncEnabled || !navigator.onLine) return { success: false, reason: 'offline' };
    //
    // try {
    //   const pendingOps = await collections.list('opsQueue', { limit: batchSize });
    //   if (pendingOps.length === 0) return { success: true, processed: 0 };
    //
    //   // TODO: Replace with actual API endpoint
    //   const response = await fetch('/api/sync/ops', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'Authorization': `Bearer ${getAuthToken()}`
    //     },
    //     body: JSON.stringify({
    //       orgId: getOrgId(),
    //       vesselId,
    //       ops: pendingOps.map(op => ({
    //         id: op.id,
    //         collection: op.collection,
    //         action: op.action,
    //         recordId: op.recordId,
    //         payload: op.payload,
    //         createdAt: op.createdAt
    //       }))
    //     })
    //   });
    //
    //   if (!response.ok) {
    //     throw new Error(`Sync failed: ${response.status} ${response.statusText}`);
    //   }
    //
    //   const result = await response.json();
    //   const { results = [], conflicts = [] } = result;
    //
    //   // Remove successfully synced operations
    //   const successfulOps = results.filter(r => r.status === 'success');
    //   for (const op of successfulOps) {
    //     await collections.delete('opsQueue', op.opId);
    //   }
    //
    //   // Handle conflicts (store for user resolution)
    //   for (const conflict of conflicts) {
    //     await collections.upsert('meta', createBaseRecord({
    //       id: `conflict_${conflict.recordId}`,
    //       vesselId,
    //       data: conflict,
    //       origin: 'sync_conflict'
    //     }));
    //   }
    //
    //   setPendingOps(prev => Math.max(0, prev - successfulOps.length));
    //
    //   return {
    //     success: true,
    //     processed: successfulOps.length,
    //     conflicts: conflicts.length,
    //     failed: results.filter(r => r.status !== 'success').length
    //   };
    //
    // } catch (err) {
    //
    //   throw err;
    // }
  };

  // Phase 2.6: Pull changes temporarily disabled - will restore in Phase 3
  // Pull changes from server
  const pullChanges = async () => {
    console.warn('Phase 2.6: pullChanges disabled - will return in Phase 3');
    return { success: false, reason: 'disabled' };

    // if (!isSyncEnabled || !navigator.onLine) return { success: false, reason: 'offline' };
    //
    // try {
    //   // Get last sync cursor
    //   const statusRecord = await collections.get('meta', 'sync_status');
    //   const cursor = statusRecord?.data?.cursor || '';

    //   // TODO: Replace with actual API endpoint
    //   const response = await fetch(`/api/sync/changes?orgId=${getOrgId()}&vesselId=${vesselId}&cursor=${cursor}`, {
    //     headers: {
    //       'Authorization': `Bearer ${getAuthToken()}`
    //     }
    //   });
    //
    //   if (!response.ok) {
    //     throw new Error(`Pull failed: ${response.status} ${response.statusText}`);
    //   }
    //
    //   const { cursor: newCursor, records = [] } = await response.json();
    //
    //   // Apply changes to local collections
    //   let applied = 0;
    //   for (const record of records) {
    //     if (record.deleted) {
    //       await collections.delete(record.collection, record.id);
    //     } else {
    //       // Use protected merge for requirements-origin records
    //       const existing = await collections.get(record.collection, record.id);
    //       const payload = existing && existing.origin === 'requirements'
    //         ? protectedMerge(record.collection, existing, record.payload)
    //         : record.payload;
    //
    //       await collections.upsert(record.collection, {
    //         ...payload,
    //         serverUpdatedAt: record.serverUpdatedAt
    //       });
    //     }
    //     applied++;
    //   }
    //
    //   // Update sync cursor
    //   await collections.upsert('meta', createBaseRecord({
    //     id: 'sync_status',
    //     vesselId,
    //     data: {
    //       cursor: newCursor,
    //       lastSync: new Date().toISOString(),
    //       lastPull: new Date().toISOString()
    //     },
    //     origin: 'sync_status'
    //   }));
    //
    //   return { success: true, applied };
    //
    // } catch (err) {
    //
    //   throw err;
    // }
  };

  // Phase 2.6: Full sync temporarily disabled - will restore in Phase 3
  // Full sync: drain queue then pull changes
  const syncNow = async () => {
    console.warn('Phase 2.6: syncNow disabled - will return in Phase 3');
    return { success: false, reason: 'disabled' };

    // if (syncing || !isSyncEnabled || !navigator.onLine) return;
    //
    // setSyncing(true);
    // setError(null);
    //
    // try {
    //   // First drain outgoing operations
    //   const drainResult = await drainQueue();
    //
    //   // Then pull incoming changes
    //   const pullResult = await pullChanges();
    //
    //   // Update last sync time
    //   const now = new Date();
    //   setLastSync(now);
    //
    //   await collections.upsert('meta', createBaseRecord({
    //     id: 'sync_status',
    //     vesselId,
    //     data: {
    //       lastSync: now.toISOString(),
    //       lastDrain: drainResult.success ? now.toISOString() : null,
    //       lastPull: pullResult.success ? now.toISOString() : null
    //     },
    //     origin: 'sync_status'
    //   }));
    //
    //   return {
    //     success: true,
    //     drained: drainResult.processed || 0,
    //     pulled: pullResult.applied || 0,
    //     conflicts: drainResult.conflicts || 0
    //   };
    //
    // } catch (err) {
    //
    //   setError(err.message);
    //   throw err;
    // } finally {
    //   setSyncing(false);
    // }
  };

  return {
    syncing,
    lastSync,
    pendingOps,
    error,
    isSyncEnabled,
    queueOperation,
    syncNow,
    drainQueue,
    pullChanges
  };
};

// Helper functions
const generatePayloadHash = (payload) => {
  // Simple hash for change detection
  return btoa(JSON.stringify(payload)).slice(0, 16);
};

const getAuthToken = () => {
  // TODO: Implement proper token management
  return localStorage.getItem('auth_token') || '';
};

const getOrgId = () => {
  // TODO: Get from license/org context
  return 'default_org';
};

// Protected merge for requirements-origin records (from Phase 1)
const protectedMerge = (collection, existing, incoming) => {
  // Import from our Phase 1 implementation
  const USER_MANAGED_FIELDS = {
    equipment: ['serialNumber', 'qrCode', 'location', 'notes'],
    procedure: ['notes'],
    drill: ['location', 'notes'],
    training: ['evidence', 'attachments', 'notes'],
    inspection: ['assignedTo', 'notes']
  };

  const userFields = USER_MANAGED_FIELDS[collection] || [];
  const preserve = {};
  
  userFields.forEach(field => {
    if (existing && existing[field] !== undefined) {
      preserve[field] = existing[field];
    }
  });

  return {
    ...incoming,
    ...preserve,
    mergedAt: new Date().toISOString()
  };
};